import { FC, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';

import { ScheduleAvailType } from '@/redux/slices/slice_types';
import { getAvailSourceByType } from '@/components/Focus/utils/newScheduleAvails';
import { useFormatAvailDisplayLabel } from '@/components/Focus/hooks/useFormatAvailDisplayLabel';
import { FormControl, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useAvailFiltering } from '@/components/Focus/hooks/useAvailFiltering';
import { StyledScroll } from '../NewScheduleAvails/AvailListContainer';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { EllipsisTooltipTypography } from '@/components/_common/EllipsisTooltipTypography';

export type IncrementalSelectionState = {
  add: string[];
  remove: string[];
};

type LaunchApplyIncrementSectionProps = {
  tab: ScheduleAvailType;
  value: IncrementalSelectionState;
  originalRights: string[];
  rightsOptions?: string[];
  onChange: (next: IncrementalSelectionState) => void;
};

const getDisplayedRights = ({ originalRights, state }: { originalRights: string[]; state: IncrementalSelectionState }): string[] => {
  const seen = new Set<string>();
  const displayedRights: string[] = [];

  state.add.forEach((right) => {
    if (seen.has(right)) return;
    seen.add(right);
    displayedRights.push(right);
  });

  originalRights.forEach((right) => {
    if (seen.has(right)) return;
    seen.add(right);
    displayedRights.push(right);
  });

  return displayedRights;
};

const LaunchApplyIncrementSection: FC<LaunchApplyIncrementSectionProps> = ({ tab, value, originalRights, rightsOptions, onChange }) => {
  const displayedRights = useMemo(() => getDisplayedRights({ originalRights, state: value }), [originalRights, value]);
  const [query, setQuery] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const formatDisplayLabel = useFormatAvailDisplayLabel();
  const formatDisplayLabelForType = useMemo(() => (code: string) => formatDisplayLabel(code, tab), [formatDisplayLabel, tab]);

  const { groupRights, rightsList } = useMemo(() => {
    const initial = getAvailSourceByType(
      tab === 'territories' ? 'ungroupTer' : tab,
      displayedRights.map((r) => ({ code: r })),
      displayedRights.map((r) => ({ code: r }))
    );
    return tab === 'territories'
      ? { groupRights: initial.groupRights, rightsList: initial.rightsList?.filter((r) => displayedRights.includes(r)) }
      : initial;
  }, [displayedRights, tab]);

  const { filteredRights } = useAvailFiltering({
    formatDisplayLabel: formatDisplayLabelForType,
    groupRights,
    isTerritoriesWithGroups: false,
    query,
    rightsList,
    rightsOptions
  });

  const displayFilteredRights = useMemo(() => {
    filteredRights.sort((a, b) => {
      const labelA = formatDisplayLabelForType(a);
      const labelB = formatDisplayLabelForType(b);
      return labelA.localeCompare(labelB);
    });
    return filteredRights;
  }, [filteredRights]);

  return (
    <StyledContent>
      <StyledContainer>
        <div>
          <ActionsRow>
            <FormControl variant="outlined" sx={(theme) => ({ mb: theme.spacing(2), maxWidth: '50%', flexGrow: 1 })}>
              <InputLabel htmlFor="find-avails-input">Find...</InputLabel>
              <SearchInput
                id="find-avails-input"
                label="Find..."
                placeholder="Find..."
                onChange={(event) => setQuery(event.target.value)}
                value={query}
                startAdornment={
                  <InputAdornment position="start">
                    <PolarisIcon icon="search" height={14} width={14} />
                  </InputAdornment>
                }
              />
            </FormControl>
          </ActionsRow>
        </div>
        <StyledScroll ref={scrollContainerRef} data-type={tab}>
          <StyledChildren>
            {displayFilteredRights.map((right) => (
              <LanguageBar
                key={`${tab}-${right}`}
                selected={value.remove.includes(right)}
                onClick={() =>
                  onChange({ ...value, remove: value.remove.includes(right) ? value.remove.filter((r) => r !== right) : [...value.remove, right] })
                }
              >
                <LanguageMainContent>
                  <TextWrapper>
                    <EllipsisTooltipTypography>{formatDisplayLabelForType(right)}</EllipsisTooltipTypography>
                  </TextWrapper>
                </LanguageMainContent>
              </LanguageBar>
            ))}
          </StyledChildren>
        </StyledScroll>
      </StyledContainer>
    </StyledContent>
  );
};

export default LaunchApplyIncrementSection;

const StyledContent = styled.div`
  flex: 1;
  min-height: 536px;
  max-height: 536px;
  display: flex;
  flex-direction: column;
`;

const ActionsRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const SearchInput = styled(OutlinedInput)(({ theme }) => ({
  width: '100%',
  borderRadius: 6,
  backgroundColor: (theme as FocusTheme).palette.neutral[90],
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: (theme as FocusTheme).palette.neutral[60],
    borderWidth: 1
  },
  '& .MuiInputAdornment-root': {
    color: (theme as FocusTheme).palette.neutral[30]
  }
}));

export const StyledContainer = styled('div')`
  background: ${({ theme }) => (theme as FocusTheme).palette.neutral[90]};
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: 10px;
  min-width: 0;
`;

const LanguageBar = styled('div')<{ selected?: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  cursor: 'pointer',
  padding: theme.spacing(2),
  margin: theme.spacing(0, 0, 0.5),
  borderRadius: 6,
  border: `1px solid ${theme.palette.primary.main}`,
  backgroundColor: selected ? theme.palette.secondary.light + '40' : theme.palette.neutral[80],
  transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
  '&:hover': {
    background: selected ? theme.palette.secondary.light + '80' : theme.palette.neutral[60]
  }
}));

const LanguageMainContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  gap: theme.spacing(2)
}));

const TextWrapper = styled('div')({
  flex: '1 1 0',
  minWidth: 0,
  overflow: 'hidden'
});

export const StyledChildren = styled('div')`
  display: flex;
  flex-wrap: wrap;
  row-gap: ${({ theme }) => theme.spacing(1)};
  column-gap: ${({ theme }) => theme.spacing(1)};
  > div {
    width: calc(33% - ${({ theme }) => theme.spacing(0.5)});
  }
`;
